import { HttpErrorResponse } from '@angular/common/http';
import { inject, provideAppInitializer } from '@angular/core';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import * as Sentry from "@sentry/angular";
import { lastValueFrom, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { environment } from "../../../../environments/environment";
import { User } from '../../../auth/models';
import { AuthService, JwtService } from '../../../auth/services';
import { RestrictedParentsService } from '../../../common/services/restricted-parents/restricted-parents.service';
import { ThemePreference, ThemeService } from '../../../common/services/theme.service';
import { ThemePreferenceWriterService } from '../../../v2/services/theme-preference-writer.service';
import { UiScale, UiScaleService } from '../../../v2/services/ui-scale.service';
import { ListAutoLoadService } from '../../../v2/shared/load-more/list-auto-load.service';
import { UiVersion, UiVersionService } from '../../../v2/services/ui-version.service';

const jwtHelper = new JwtHelperService();

/** What Sentry may know about the signed-in user: the id, role and company type. No names, emails or company names. */
export function sentryUserFrom(user: User): Sentry.User {
    return {
        id: String(user.id),
        role: user.role,
        company_type: user.company?.type,
    } as Sentry.User;
}

export function verifyToken(
    auth: AuthService,
    jwt: JwtService,
    router: Router,
    restrictedParents: RestrictedParentsService,
    uiVersion: UiVersionService,
    uiScale: UiScaleService,
    themeService: ThemeService,
    listAutoLoad?: ListAutoLoadService,
    themeWriter?: ThemePreferenceWriterService,
) {
    return function () {
        const token = jwt.getToken();

        // If no token exists, skip verification
        if (!token) {
            return;
        }

        // Check if token is expired using JwtHelperService before making API call
        let expired: boolean;
        try {
            expired = jwtHelper.isTokenExpired(token);
        } catch (e) {
            // Invalid token format: clear it. The flag lets AuthenticatedUserGuard say
            // "session expired" on the sign-in page.
            jwt.dropExpiredToken();
            return;
        }
        // The access token lives 15 minutes: an ended one is renewed with the refresh token
        // (30 days). Only with no live refresh token is the session over, without an API call.
        if (expired && !jwt.canRefresh()) {
            jwt.dropExpiredToken();
            return;
        }
        const verified = expired
            ? jwt.refresh().pipe(switchMap(() => jwt.verifyToken()))
            : jwt.verifyToken();

        // lastValueFrom with a default keeps toPromise()'s 'undefined on empty' (no EmptyError).
        return lastValueFrom(verified.pipe(
            catchError((error) => {
                // Handle maintenance mode during app initialization
                if (error instanceof HttpErrorResponse && (error.status === 503 || (error.error && error.error.maintenance_mode))) {
                    // Redirect to maintenance mode page and continue app initialization
                    setTimeout(() => router.navigate(['/maintenance-mode']), 0);
                    return of(false);
                }

                // Expected errors (401 Unauthorized for invalid/expired tokens)
                // These are silently handled - token is already destroyed in jwt.service
                if (error instanceof HttpErrorResponse && error.status === 401) {
                    jwt.dropExpiredToken();
                    return of(false);
                }

                // For unexpected errors, log to console for debugging
                if (!(error instanceof HttpErrorResponse) || error.status >= 500) {
                    console.error('Unexpected error during token verification:', error);
                }

                return of(false);
            }),
            switchMap((data) => {
                if (data) {
                    // First update the company settings
                    auth.getCurrentCompany().update(data['company']);

                    // Update user data but preserve the company reference
                    const userData = { ...data['user'] };
                    delete userData.company; // Remove company from user data to prevent overriding
                    auth.getCurrentUser().update(userData);

                    // Ensure user.company points to the same instance as AuthService company
                    auth.getCurrentUser().company = auth.getCurrentCompany();

                    // Seed the UI-version signal from the raw profile payload so
                    // it is right on first paint. Read raw rather than through
                    // UserSettings, which does not carry ui_version. A ?ui=
                    // query parameter overrides this for the page load only. Never
                    // chosen (null) means v2, the default (owner, 2026-10-06).
                    uiVersion.initFrom(
                        (data['user']?.['settings']?.['ui_version'] as UiVersion | null) ?? null);

                    // UserSetting.ui_scale, the v2 text-size choice — same raw-payload
                    // read as ui_version above, so first paint is right without a
                    // second round trip.
                    uiScale.initFrom(
                        (data['user']?.['settings']?.['ui_scale'] as UiScale) ?? 'default');

                    // UserSetting.list_auto_load, "Load automatically" on every paged
                    // v2 list (ui-rules §4a) — same raw-payload read.
                    listAutoLoad?.initFrom(data['user']?.['settings']?.['list_auto_load'] === true);

                    // UserSetting.theme_preference — same raw-payload read, seeding
                    // ThemeService (never the API: it stays local-only, shared with
                    // classic) so a fresh device shows the saved theme rather than
                    // the localStorage-less 'light' fallback (audit C11's other
                    // half: the write was fixed in the v2 account menu, this is the
                    // read). Left alone when the payload carries no settings at all.
                    const savedTheme = data['user']?.['settings']?.['theme_preference'] as ThemePreference;
                    themeService.seedFromAccount(savedTheme);

                    // D-477: Light and Dark are the only choices. A saved 'auto' is
                    // resolved once to what the person sees now and saved, so the
                    // theme does not flip. Not awaited: bootstrap must not wait on it.
                    const resolvedTheme = themeService.autoToResolve?.(savedTheme) ?? null;
                    if (resolvedTheme && themeWriter) {
                        void themeWriter.setTheme(resolvedTheme);
                    }

                    if (data['user']['user_otp_method'] && data['token'] && data['token']['token']) {
                        let decodedJwt = jwtHelper.decodeToken(data['token']['token']);
                        // A remembered device signs in without a code: its token carries
                        // trusted_device_id instead of otp_device_id, and is verified too.
                        auth.getCurrentUser().update({
                            'is_otp_confirmed': !!(decodedJwt['otp_device_id'] || decodedJwt['trusted_device_id']),
                        });
                    }
                    // set user info in Sentry
                    let user = auth.getCurrentUser();
                    const scope = Sentry.getCurrentScope();
                    scope.setTag("env-api-url", environment.apiUrl);
                    // Personal data stays out of Sentry: id, role and company type only.
                    scope.setUser(sentryUserFrom(user));

                    // Prime the restricted-parents cache and BLOCK bootstrap
                    // on it — role checks like `user.canManage(tender)` are
                    // synchronous, so first-paint must wait for this set to
                    // be populated. Errors are swallowed by the service
                    // (fail-closed: empty set + loadStatus='error').
                    return restrictedParents.load().pipe(
                        catchError(() => of(null)),
                        switchMap(() => of(auth.getCurrentUser())),
                    );
                }
                return of(auth.getCurrentUser());
            })
        ), { defaultValue: undefined });
    };
}

export const AppInitializerProvider = provideAppInitializer(() => { //: FactoryProvider
        const initializerFn = (verifyToken)(
            inject(AuthService),
            inject(JwtService),
            inject(Router),
            inject(RestrictedParentsService),
            inject(UiVersionService),
            inject(UiScaleService),
            inject(ThemeService),
            inject(ListAutoLoadService),
            inject(ThemePreferenceWriterService),
        );
        return initializerFn();
      });


// export const AppInitializerProvider: FactoryProvider = {
//     provide: APP_INITIALIZER,
//     useFactory: verifyToken,
//     deps: [AuthService, JwtService],
//     multi: true
// };


